import { FC, useMemo } from 'react';
import { css } from '@emotion/react';
import { colors, spacing } from '../../../styles';
import { SENTIMENT_COLORS } from './shared/constants';

interface OptionsFlow {
  type: 'CALL' | 'PUT';
  strike: number;
  volume?: number;
  open_interest?: number;
  expiration?: string;
}

interface TechnicalIndicators {
  insights: string;
}

interface OptionsAnalysis {
  flows?: OptionsFlow[];
  put_call_ratio?: number;
  implied_volatility?: number;
  unusual_activity?: string[];
}

interface InsiderActivity {
  net_activity?: number;
  dollars_bought?: number;
  dollars_sold?: number;
  insights?: string;
}

interface TechnicalAnalysisData {
  symbol?: string;
  technical_indicators?: TechnicalIndicators;
  options_analysis?: OptionsAnalysis;
  insider_activity?: InsiderActivity;
  combined_analysis?: string;
  error?: string;
}

interface ParsedToolOutput {
  type?: string;
  status?: string;
  message?: string;
  [key: string]: any;
}

interface ToolRendererProps {
  toolName: string;
  output: ParsedToolOutput;
  isLoading?: boolean;
}

const LoadingState: FC<{ message: string }> = ({ message }) => (
  <div
    css={css`
      font-size: 14px;
      color: ${SENTIMENT_COLORS.muted};
    `}
  >
    {message}
  </div>
);

// Helper functions for formatting
const formatCurrency = (amount: number) => {
  if (amount >= 1e6) return `$${(amount / 1e6).toFixed(1)}M`;
  if (amount >= 1e3) return `$${(amount / 1e3).toFixed(1)}K`;
  return `$${amount.toLocaleString()}`;
};

const getTopOption = (flows: OptionsFlow[]) => {
  if (!flows || flows.length === 0) return null;
  const topFlow = flows[0];
  const volume = topFlow.volume || 0;
  return {
    flow: topFlow,
    volumeFormatted: formatCurrency(volume),
  };
};

// Parse RSI from insights string or direct value
const parseRSI = (insights: string, details?: any) => {
  // Check for direct RSI value first (from structured data)
  const directRSI = details?.rsi ?? details?.RSI;
  let value: number | null = null;

  if (typeof directRSI === 'number' && !isNaN(directRSI)) {
    value = directRSI;
  } else {
    const rsiMatch = insights.match(/RSI:\s*(\d+\.?\d*)/);
    if (!rsiMatch) return null;
    value = parseFloat(rsiMatch[1]);
  }

  if (value === null) return null;

  let level = 'Neutral';
  let color: string = SENTIMENT_COLORS.neutral;
  let icon = '⚪';

  if (value >= 70) {
    level = 'Overbought';
    color = SENTIMENT_COLORS.neutral;
    icon = '🟡';
  } else if (value <= 30) {
    level = 'Oversold';
    color = SENTIMENT_COLORS.positive;
    icon = '🟢';
  }

  return { value, level, color, icon };
};

// Render a single symbol's technical analysis
const SingleTechnicalDisplay: FC<{ data: TechnicalAnalysisData }> = ({ data }) => {
  // Handle error case
  if (data?.error) {
    return (
      <div
        css={css`
          font-size: 14px;
          color: ${colors.berry};
        `}
      >
        {data.symbol && <strong>{data.symbol}: </strong>}
        {data.error}
      </div>
    );
  }

  // Unwrap technical indicators from nested analysis structure
  const techIndicators = (data as any)?.analysis?.technical_indicators || data?.technical_indicators;
  const rsiData = techIndicators?.insights ? parseRSI(techIndicators.insights, techIndicators?.details) : null;
  const topOption = data.options_analysis?.flows ? getTopOption(data.options_analysis.flows) : null;

  return (
    <div
      css={css`
        font-size: 14px;
        color: ${colors.liquorice};
        line-height: 1.6;
        padding: ${spacing.deci};

        html[data-theme='dark'] & {
          color: ${colors.sugar};
        }
      `}
    >
      {/* Title */}
      {data.symbol && (
        <div
          css={css`
            font-weight: 600;
            font-size: 15px;
            margin-bottom: ${spacing.deci};
            color: ${SENTIMENT_COLORS.muted};
          `}
        >
          {data.symbol} Technical Analysis
        </div>
      )}

      {/* Single Column Layout */}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: ${spacing.centi};
        `}
      >
        {/* RSI Row */}
        <div
          css={css`
            display: flex;
            justify-content: space-between;
            align-items: center;
          `}
        >
          <span
            css={css`
              color: ${SENTIMENT_COLORS.muted};
            `}
          >
            RSI
          </span>
          <span
            css={css`
              font-weight: 500;
              color: ${rsiData?.color || SENTIMENT_COLORS.muted};
            `}
          >
            {rsiData?.value || '--'} {rsiData?.icon || '⚪'} {rsiData?.level || 'Neutral'}
          </span>
        </div>

        {/* Trend Row */}
        {(techIndicators?.trend || techIndicators?.insights) && (
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              gap: ${spacing.deci};
            `}
          >
            <span
              css={css`
                color: ${SENTIMENT_COLORS.muted};
                flex-shrink: 0;
              `}
            >
              Trend
            </span>
            <span
              css={css`
                text-align: right;
              `}
            >
              {(() => {
                // Check for direct trend field first
                if (techIndicators?.trend) {
                  return techIndicators.trend;
                }
                const insights = techIndicators?.insights || '';
                const trendMatch = insights.match(/Short-term trend:\s*(bullish|bearish|neutral)/i);
                const positionMatch = insights.match(/(above|below)\s+(EMA\d+|SMA\d+|\d+-day\s+(?:EMA|SMA))/i);

                if (positionMatch && trendMatch) {
                  return `${positionMatch[1]} ${positionMatch[2]}. Short-term trend: ${trendMatch[1]}`;
                } else if (trendMatch) {
                  return `Short-term trend: ${trendMatch[1]}`;
                } else if (positionMatch) {
                  return `${positionMatch[1]} ${positionMatch[2]}`;
                }
                return 'Neutral';
              })()}
            </span>
          </div>
        )}

        {/* P/C Ratio Row */}
        {data.options_analysis?.put_call_ratio && (
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              align-items: center;
            `}
          >
            <span
              css={css`
                color: ${SENTIMENT_COLORS.muted};
              `}
            >
              P/C Ratio
            </span>
            <span
              css={css`
                font-weight: 500;
                color: ${data.options_analysis.put_call_ratio > 1
                  ? SENTIMENT_COLORS.negative
                  : SENTIMENT_COLORS.positive};
              `}
            >
              {data.options_analysis.put_call_ratio.toFixed(2)}{' '}
              <span
                css={css`
                  color: ${SENTIMENT_COLORS.muted};
                  font-weight: 400;
                `}
              >
                ({data.options_analysis.put_call_ratio > 1 ? 'Bearish' : 'Bullish'})
              </span>
            </span>
          </div>
        )}

        {/* Implied Vol Row */}
        {data.options_analysis?.implied_volatility &&
          typeof data.options_analysis.implied_volatility === 'number' &&
          !isNaN(data.options_analysis.implied_volatility) && (
            <div
              css={css`
                display: flex;
                justify-content: space-between;
                align-items: center;
              `}
            >
              <span
                css={css`
                  color: ${SENTIMENT_COLORS.muted};
                `}
              >
                Implied Vol
              </span>
              <span>{(data.options_analysis.implied_volatility * 100).toFixed(1)}%</span>
            </div>
          )}

        {/* Insider Activity Row */}
        {!!data.insider_activity?.net_activity && (
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              align-items: center;
            `}
          >
            <span
              css={css`
                color: ${SENTIMENT_COLORS.muted};
              `}
            >
              Insider Activity
            </span>
            <span
              css={css`
                font-weight: 500;
                color: ${data.insider_activity.net_activity > 0
                  ? SENTIMENT_COLORS.positive
                  : SENTIMENT_COLORS.negative};
              `}
            >
              {data.insider_activity.net_activity >= 0 ? '+' : ''}
              {formatCurrency(data.insider_activity.net_activity)}{' '}
              <span
                css={css`
                  color: ${SENTIMENT_COLORS.muted};
                  font-weight: 400;
                `}
              >
                net {data.insider_activity.net_activity > 0 ? 'buying' : 'selling'}
              </span>
            </span>
          </div>
        )}

        {/* Top Option Row */}
        {topOption && (
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding-top: ${spacing.centi};
              border-top: 1px solid ${colors.cloud};

              html[data-theme='dark'] & {
                border-top-color: ${colors.tungsten};
              }
            `}
          >
            <span
              css={css`
                color: ${SENTIMENT_COLORS.muted};
              `}
            >
              Top Option
            </span>
            <span>
              {topOption.flow.expiration || 'Jan 17'} ${topOption.flow.strike}
              {topOption.flow.type === 'CALL' ? 'C' : 'P'}{' '}
              <span
                css={css`
                  color: ${SENTIMENT_COLORS.muted};
                `}
              >
                ({topOption.volumeFormatted} vol)
              </span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export const TechnicalAnalysisRenderer: FC<ToolRendererProps> = ({ output, isLoading }) => {
  // Handle both string and object outputs with proper typing - must be before early returns
  const parsedOutput = useMemo((): any => {
    if (typeof output === 'string') {
      try {
        return JSON.parse(output);
      } catch {
        return null;
      }
    }
    return output;
  }, [output]);

  if (isLoading) {
    return <LoadingState message="Analyzing technicals..." />;
  }

  // Handle batch format: {"technical_analysis": [...], "metadata": {...}}
  // Check both raw output and parsed output for batch format
  const batchData =
    parsedOutput && typeof parsedOutput === 'object' && Array.isArray(parsedOutput.technical_analysis)
      ? parsedOutput.technical_analysis
      : null;

  if (batchData) {
    const results = batchData as TechnicalAnalysisData[];
    return (
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: ${spacing.deci};
        `}
      >
        <div
          css={css`
            font-size: 13px;
            color: ${SENTIMENT_COLORS.muted};
            margin-bottom: ${spacing.milli};
          `}
        >
          Analyzed {results.length} symbol{results.length !== 1 ? 's' : ''}
        </div>
        {results.map((item: TechnicalAnalysisData, index: number) => {
          // Unwrap nested analysis structure if present
          const techData = (item as any).analysis?.technical_indicators
            ? { ...item, technical_indicators: (item as any).analysis.technical_indicators }
            : item;
          return (
            <div
              key={techData.symbol || index}
              css={css`
                ${index > 0
                  ? `
                    border-top: 1px solid ${colors.cloud};
                    padding-top: ${spacing.deci};

                    html[data-theme='dark'] & {
                      border-color: ${colors.tungsten};
                    }
                  `
                  : ''}
              `}
            >
              <SingleTechnicalDisplay data={techData} />
            </div>
          );
        })}
      </div>
    );
  }

  if (!parsedOutput) {
    return <LoadingState message="Technical analysis completed" />;
  }

  // Single symbol legacy format
  return <SingleTechnicalDisplay data={parsedOutput as TechnicalAnalysisData} />;
};
